// src/components/views/ProjectView.tsx
import React, { useState, useEffect, useMemo } from 'react'
import { useDeleteProjectMutation, useGetProjectQuery } from '../../../services/projectsApi'
import { useAppSelector, useAppDispatch, useAnnotationStats } from '../../../store/hooks'
import { selectSelectedProjectId } from '../../../store/slices/projectSlice'
import { selectSelectedModelId, setSelectedModelId } from '../../../store/slices/modelSlice'
import { useNavigate } from 'react-router-dom'
import { useGetContentMetadataQuery } from '../../../services/contentApi'
import { selectDatasetSources } from '../../../store/slices/datasetSlice'
import ConfirmDialog from '../../modals/ConfirmDialog'
import ErrorDialog from '../../modals/ErrorDialog'
import { useFetchModelsQuery } from '../../../services/modelsApi'
import { Model as ImportedModel } from '../../../types/models/model'
import UploadWizard from '../../wizards/UploadWizard'
import {
  DashboardContainer,
  ScrollableContent,
  DashboardContent,
  StatsRow,
  StatsCol,
} from './styles'
import {
  ProjectInformationPanel,
  DatasetAnalyticsPanel,
  ClassDistributionSection,
  ModelStatisticsSection,
  ModelPerformanceSection,
} from './components'

interface ModelMetrics {
  'validation:mAP50-95': number
  'validation:precision': number
  'validation:recall': number
  [key: string]: number
}

interface Model extends Omit<ImportedModel, 'metrics'> {
  metrics?: ModelMetrics
}

const ProjectOverview: React.FC = () => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const selectedProjectId = useAppSelector(selectSelectedProjectId)
  const selectedModelId = useAppSelector(selectSelectedModelId)
  const datasetSources = useAppSelector(selectDatasetSources)

  const {
    data: projectData,
    isLoading: isLoadingProject,
    isFetching: isFetchingProject,
  } = useGetProjectQuery(selectedProjectId || '', {
    skip: !selectedProjectId,
  })
  const {
    data: annoStats,
    isLoading: isLoadingStats,
    isFetching: isFetchingStats,
  } = useAnnotationStats(selectedProjectId || '', projectData?.datasetid || '', datasetSources)

  useEffect(() => {
    const pathSegments = window.location.pathname.split('/')
    if (pathSegments.length === 3 && pathSegments[1] === 'overview') {
      navigate(`/overview/${selectedProjectId}/overview`)
    }
  }, [navigate, selectedProjectId])

  const { data: metadata } = useGetContentMetadataQuery(
    {
      project_id: selectedProjectId || '',
    },
    { skip: !selectedProjectId }
  )

  const [deleteProject] = useDeleteProjectMutation()
  const [showConfirmDelete, setShowConfirmDelete] = useState(false)
  const [showUploadDataModal, setShowUploadDataModal] = useState(false)
  const [showUploadWizard, setShowUploadWizard] = useState(false)
  const [selectedDataset, setSelectedDataset] = useState<string>('all')
  const [uploading, setUploading] = useState(false)
  const [hasUserDismissedUpload, setHasUserDismissedUpload] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [showError, setShowError] = useState(false)

  const { data: modelsData } = useFetchModelsQuery(
    { project_id: selectedProjectId || '' },
    { skip: !selectedProjectId }
  )

  useEffect(() => {
    if (annoStats?.statistics && !hasUserDismissedUpload) {
      const totalImages =
        (annoStats.statistics.total_annotated_images || 0) +
        (annoStats.statistics.total_unannotated_images || 0)
      if (totalImages === 0 && !uploading) {
        setShowUploadDataModal(true)
      }
    }
  }, [annoStats?.statistics, projectData, uploading, hasUserDismissedUpload])

  useEffect(() => {
    // Skip if we already have a selected model or if models data isn't loaded yet
    const selectedModelIsInProject = modelsData?.models?.some(
      (model) => model.id === selectedModelId
    )
    if ((selectedModelId && selectedModelIsInProject) || !modelsData?.models) {
      return
    }

    // First check if there's a stored model for this project
    const storedModels = JSON.parse(localStorage.getItem('selectedModelsByProject') || '{}')
    const storedModelId = storedModels[selectedProjectId || '']

    // If we have a stored model and it exists in the current project's models, use it
    if (storedModelId && modelsData.models.some((model) => model.id === storedModelId)) {
      dispatch(
        setSelectedModelId({
          modelId: storedModelId,
          projectId: selectedProjectId || '',
        })
      )
      return
    }

    // If no stored model or it doesn't exist in current models, select based on priority
    if (modelsData.models.length) {
      // Priority 1: Most recent batch.ended_at
      const modelsWithBatches = modelsData.models.filter((model) => model.batch?.ended_at)
      if (modelsWithBatches.length > 0) {
        const mostRecentModel = modelsWithBatches.reduce((latest, current) => {
          const latestDate = new Date(latest.batch.ended_at)
          const currentDate = new Date(current.batch.ended_at)
          return currentDate > latestDate ? current : latest
        })
        dispatch(
          setSelectedModelId({
            modelId: mostRecentModel.id,
            projectId: selectedProjectId || '',
          })
        )
        return
      }

      // Priority 2: IN_SERVICE
      const inServiceModel = modelsData.models.find(
        (model) => model.deployment.status === 'IN_SERVICE'
      )
      if (inServiceModel) {
        dispatch(
          setSelectedModelId({
            modelId: inServiceModel.id,
            projectId: selectedProjectId || '',
          })
        )
        return
      }

      // Priority 3: CREATING
      const creatingModel = modelsData.models.find(
        (model) => model.deployment.status === 'CREATING'
      )
      if (creatingModel) {
        dispatch(
          setSelectedModelId({
            modelId: creatingModel.id,
            projectId: selectedProjectId || '',
          })
        )
        return
      }

      // Priority 4: INITIALIZED
      const initializedModel = modelsData.models.find(
        (model) => model.deployment.status === 'INITIALIZED'
      )
      if (initializedModel) {
        dispatch(
          setSelectedModelId({
            modelId: initializedModel.id,
            projectId: selectedProjectId || '',
          })
        )
        return
      }

      // Priority 5: First model in the list
      dispatch(
        setSelectedModelId({
          modelId: modelsData.models[0].id,
          projectId: selectedProjectId || '',
        })
      )
    }
  }, [modelsData?.models, dispatch, selectedProjectId, selectedModelId])

  const handleUploadData = () => {
    setUploading(true)
    setShowUploadDataModal(false)
    setShowUploadWizard(true)
  }

  const handleDismissUpload = () => {
    setShowUploadDataModal(false)
    setHasUserDismissedUpload(true)
  }

  const navigateToDatasets = () => {
    setShowUploadWizard(false)
  }

  const handleDeleteProject = async () => {
    try {
      await deleteProject(selectedProjectId || '').unwrap()
      navigate('/projects')
    } catch (err) {
      setErrorMessage(
        err
          ? (err as any).data?.message || 'Failed to delete project'
          : 'An unexpected error occurred'
      )
      setShowError(true)
    }
  }

  const countTrainedAndDeployedModels = (
    models: ImportedModel[]
  ): { trained: number; deployed: number } => {
    return models?.reduce(
      (acc, model) => {
        if (model.deployment?.status.toLowerCase() === 'in_service') {
          acc.deployed++
          acc.trained++
        } else if (model.state.toLowerCase() === 'trained') {
          acc.trained++
        }
        return acc
      },
      { trained: 0, deployed: 0 }
    )
  }

  const modelCounts = modelsData?.models
    ? countTrainedAndDeployedModels(modelsData.models)
    : { trained: 0, deployed: 0 }

  const datasetChartData = {
    labels: metadata?.metadata?.datasource || [],
    datasets: [
      {
        label: 'Images',
        data:
          metadata?.metadata?.datasource?.map((source: string) => {
            return annoStats?.statistics?.datasource_distributions?.[source]?.images || 0
          }) || [],
        backgroundColor: 'rgba(13, 202, 240, 0.7)',
        borderColor: 'rgba(13, 202, 240, 1)',
        borderWidth: 1,
      },
      {
        label: 'Annotations',
        data:
          metadata?.metadata?.datasource?.map((source: string) => {
            return annoStats?.statistics?.datasource_distributions?.[source]?.annotations || 0
          }) || [],
        backgroundColor: 'rgba(25, 135, 84, 0.7)',
        borderColor: 'rgba(25, 135, 84, 1)',
        borderWidth: 1,
      },
    ],
  }

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(255, 255, 255, 0.1)',
        },
        ticks: {
          color: '#ffffff',
        },
      },
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: '#ffffff',
        },
      },
    },
    plugins: {
      legend: {
        labels: {
          color: '#ffffff',
        },
      },
    },
  }

  const modelPerformanceData = {
    labels:
      modelsData?.models
        ?.filter((model: ImportedModel) => model.state.toLowerCase() === 'trained')
        ?.map((model: ImportedModel) => model.name) || [],
    datasets: [
      {
        label: 'mAP50-95',
        data:
          modelsData?.models
            ?.filter((model: ImportedModel) => model.state.toLowerCase() === 'trained')
            ?.map((model: ImportedModel) => {
              const m = model as unknown as Model
              return Number(m.metrics?.['validation:mAP50-95'] || 0) * 100
            }) || [],
        backgroundColor: 'rgba(13, 202, 240, 0.7)',
        borderColor: 'rgba(13, 202, 240, 1)',
        borderWidth: 1,
      },
      {
        label: 'Precision',
        data:
          modelsData?.models
            ?.filter((model: ImportedModel) => model.state.toLowerCase() === 'trained')
            ?.map((model: ImportedModel) => {
              const m = model as unknown as Model
              return Number(m.metrics?.['validation:precision'] || 0) * 100
            }) || [],
        backgroundColor: 'rgba(25, 135, 84, 0.7)',
        borderColor: 'rgba(25, 135, 84, 1)',
        borderWidth: 1,
      },
      {
        label: 'Recall',
        data:
          modelsData?.models
            ?.filter((model: ImportedModel) => model.state.toLowerCase() === 'trained')
            ?.map((model: ImportedModel) => {
              const m = model as unknown as Model
              return Number(m.metrics?.['validation:recall'] || 0) * 100
            }) || [],
        backgroundColor: 'rgba(220, 53, 69, 0.7)',
        borderColor: 'rgba(220, 53, 69, 1)',
        borderWidth: 1,
      },
    ],
  }

  const modelPerformanceOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
        grid: {
          color: 'rgba(255, 255, 255, 0.1)',
        },
        ticks: {
          color: '#ffffff',
          callback: (value: number) => `${value}%`,
        },
      },
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: '#ffffff',
        },
      },
    },
    plugins: {
      legend: {
        labels: {
          color: '#ffffff',
        },
      },
      tooltip: {
        callbacks: {
          label: (context: any) => `${context.dataset.label}: ${context.parsed.y.toFixed(1)}%`,
        },
      },
    },
  }

  const isLoading = useMemo(() => {
    // Only consider loading states that affect the data we need
    const dataLoading = isLoadingStats || isLoadingProject
    const dataFetching = isFetchingStats || isFetchingProject
    const dataMissing = !annoStats?.statistics || !metadata?.metadata

    return dataLoading || (dataFetching && dataMissing)
  }, [
    isLoadingStats,
    isLoadingProject,
    isFetchingStats,
    isFetchingProject,
    annoStats?.statistics,
    metadata?.metadata,
  ])

  return (
    <DashboardContainer>
      <ScrollableContent>
        <DashboardContent>
          <StatsRow>
            <StatsCol xs={12} lg={5}>
              <ProjectInformationPanel
                projectId={projectData?.id || ''}
                description={projectData?.description || ''}
                annotationType={projectData?.annotation_type || ''}
                totalItems={projectData?.count || 0}
                updatedAt={projectData?.updated_at || ''}
              />
            </StatsCol>
            <StatsCol xs={12} lg={7}>
              <DatasetAnalyticsPanel
                loading={isLoading}
                chartData={datasetChartData}
                chartOptions={chartOptions}
              />
            </StatsCol>
          </StatsRow>

          <ClassDistributionSection
            loading={isLoading}
            selectedDataset={selectedDataset}
            onDatasetChange={setSelectedDataset}
            dataSources={metadata?.metadata?.datasource || []}
          />

          {modelsData?.models && modelsData.models.length > 0 && (
            <StatsRow>
              <StatsCol xs={12} md={3}>
                <ModelStatisticsSection
                  totalModels={modelsData.models.length}
                  trainedModels={modelCounts.trained}
                  deployedModels={modelCounts.deployed}
                />
              </StatsCol>
              <StatsCol xs={12} md={9}>
                <ModelPerformanceSection
                  loading={!modelsData?.models}
                  chartData={modelPerformanceData}
                  chartOptions={modelPerformanceOptions}
                />
              </StatsCol>
            </StatsRow>
          )}
        </DashboardContent>
      </ScrollableContent>

      <ConfirmDialog
        show={showConfirmDelete}
        onHide={() => setShowConfirmDelete(false)}
        onConfirmRight={handleDeleteProject}
        title="Delete Project"
        message="Are you sure you want to delete this project? This action cannot be undone."
        confirmRightText="Delete"
        cancelText="Cancel"
        variant="danger"
      />

      <ConfirmDialog
        show={showUploadDataModal}
        onHide={handleDismissUpload}
        onConfirmRight={handleUploadData}
        title="Upload Data"
        message="You need to upload data to do anything useful. Click here to go to the datasets page."
        confirmRightText="Upload Data"
        cancelText="Cancel"
        variant="primary"
      />

      <UploadWizard show={showUploadWizard} onHide={navigateToDatasets} />

      <ErrorDialog show={showError} onHide={() => setShowError(false)} message={errorMessage} />
    </DashboardContainer>
  )
}

export default ProjectOverview
